import type { TraitMeta } from './meta';

export type TraitRouteBase =
  | 'mlb-career'
  | 'mlb-season'
  | 'npb-career'
  | 'npb-season'
  | 'mlb-leader'
  | 'npb-leader';

type TraitPathSource = {
  trait_id: string;
  league_scope?: string | null;
  record_scope?: string | null;
};

const STRIP_PREFIXES = ['mlb_npb_', 'mlb_', 'npb_'] as const;

const TRAIT_THRESHOLD_SLUG_OVERRIDES: Record<string, number[]> = {
  mlb_batter_season_home_runs: [30, 40, 50],
  npb_batter_season_home_runs: [30, 40, 50],
  mlb_batter_season_steals: [30, 40, 50],
  npb_batter_season_steals: [30, 40, 50],
  mlb_batter_season_batting_average: [0.3, 0.33, 0.35],
  npb_batter_season_batting_average: [0.3, 0.33, 0.35],
};

export const isLeaderTraitId = (traitId: string): boolean => traitId.includes('_leader_');

export const getTraitThresholdSlugValues = (traitId: string): number[] =>
  TRAIT_THRESHOLD_SLUG_OVERRIDES[traitId] ?? [];

export const formatTraitThresholdSlugToken = (traitId: string, threshold: number): string => {
  if (traitId.endsWith('_batting_average')) return String(Math.round(threshold * 1000));
  if (Number.isInteger(threshold)) return String(Math.trunc(threshold));
  return String(Math.round(threshold * 1000) / 1000);
};

export const formatTraitThresholdNameToken = (traitId: string, threshold: number): string => {
  if (traitId.endsWith('_batting_average')) return String(Math.round(threshold * 1000));
  if (traitId.endsWith('_ops')) return threshold.toFixed(1);
  if (Number.isInteger(threshold)) return String(Math.trunc(threshold));
  return String(Math.round(threshold * 1000) / 1000);
};

export const formatTraitThresholdDescriptionToken = (traitId: string, threshold: number): string => {
  if (traitId.endsWith('_batting_average')) return threshold.toFixed(3);
  if (traitId.endsWith('_ops')) return threshold.toFixed(1);
  if (Number.isInteger(threshold)) return String(Math.trunc(threshold));
  return String(Math.round(threshold * 1000) / 1000);
};

export const parseTraitThresholdSlugToken = (
  traitId: string,
  token: string,
): number | null => {
  const thresholds = getTraitThresholdSlugValues(traitId);
  for (const threshold of thresholds) {
    if (formatTraitThresholdSlugToken(traitId, threshold) === token) return threshold;
  }
  return null;
};

export const getTraitRouteBase = (
  leagueScope: string | null | undefined,
  recordScope: string | null | undefined,
  traitId?: string | null,
): TraitRouteBase | null => {
  if (traitId && isLeaderTraitId(traitId)) {
    if (leagueScope === 'mlb') return 'mlb-leader';
    if (leagueScope === 'npb' || leagueScope === 'mlb_npb') return 'npb-leader';
    return null;
  }

  const scope = recordScope === 'season' ? 'season' : recordScope === 'career' ? 'career' : null;
  if (!scope) return null;
  if (leagueScope === 'mlb') return (`mlb-${scope}` as TraitRouteBase);
  if (leagueScope === 'npb' || leagueScope === 'mlb_npb') return (`npb-${scope}` as TraitRouteBase);
  return null;
};

const compactTraitCore = (traitId: string): string => {
  let rest = traitId;
  for (const prefix of STRIP_PREFIXES) {
    if (rest.startsWith(prefix)) {
      rest = rest.slice(prefix.length);
      break;
    }
  }

  const parts = rest.split('_');
  const careerIdx = parts.indexOf('career');
  if (careerIdx >= 0) {
    parts.splice(careerIdx, 1);
  } else {
    const seasonIdx = parts.indexOf('season');
    if (seasonIdx >= 0) parts.splice(seasonIdx, 1);
  }
  return parts.join('-');
};

export const getTraitSlug = (
  traitId: string,
  leagueScope: string | null | undefined,
): string => {
  const base = compactTraitCore(traitId);
  if (leagueScope === 'mlb_npb') return `combined-${base}`;
  return base;
};

export const getTraitPath = (
  traitId: string,
  leagueScope: string | null | undefined,
  recordScope: string | null | undefined,
): string | null => {
  const routeBase = getTraitRouteBase(leagueScope, recordScope, traitId);
  if (!routeBase) return null;
  const slug = getTraitSlug(traitId, leagueScope);
  return `/${routeBase}/${slug}`;
};

export const getTraitPathFromMeta = (trait: TraitPathSource): string | null => {
  return getTraitPath(trait.trait_id, trait.league_scope, trait.record_scope);
};

export const findTraitByRoute = (
  traits: TraitMeta[],
  routeBase: TraitRouteBase,
  traitSlug: string,
): TraitMeta | undefined => {
  return traits.find((trait) => {
    const base = getTraitRouteBase(trait.league_scope, trait.record_scope, trait.trait_id);
    if (base !== routeBase) return false;
    return getTraitSlug(trait.trait_id, trait.league_scope) === traitSlug;
  });
};
